import { isNotEmptyArray } from './array';
import { isValued } from './object';

// https://stackoverflow.com/questions/50158272/what-is-the-type-of-an-enum-in-typescript

type EnumKey<E> = keyof E;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type EnumValue<E> = string | number;
// type EnumType = Record<string, EnumValue> & { [k: number]: string };
type EnumType<E> = Record<EnumKey<E>, EnumValue<E>>;
// type EnumType<E> = Record<keyof E, number | string> & { [k: number]: keyof E };
// type EnumType<E> = Record<keyof E, number | string> & { [k: number]: string };
// type EnumType<E> = Record<EnumKey<E>, EnumValue> & { [k: number]: EnumKey<E> };

/**
 * General utility functions for web applications to streamline code and enforce consistency.
 */

/**
 * Mapping function to translate the name of enum from a string to the enum representation.
 *
 * @param myEnum the enum to search.
 * @param enumName the name of the enum to search for.
 *
 * @return the enum matching the given name or null if no match was found.
 */
export function getEnumByEnumName<E extends EnumType<E>>(myEnum: E, enumName: EnumKey<E>): EnumValue<E> | null {
  const enumMatch = myEnum[enumName];

  if (enumMatch) {
    return enumMatch;
  }

  return null;
}

/**
 * Reverse mapping function for string-based enums which returns the first enum match for a given value
 * (values can match more than one instance of an enum).  Note that enums don't have types, so we use the 'any' type.
 *
 * @param myEnum the enum to search through.
 * @param enumValue the value to search for.
 *
 * @return the enum value match or null if no match was found.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getEnumByEnumValue<E extends EnumType<E>>(myEnum: E, enumValue: EnumValue<E>): any | null {
  const enumMatches: EnumValue<E>[] = getEnumsByEnumValue(myEnum, enumValue);

  if (isNotEmptyArray(enumMatches)) {
    return enumMatches[0] as EnumValue<E>;
  }

  return null;
}

/**
 * Reverse mapping function for string-based enums which returns all enum matches for a given value (values can match
 * more than one instance of an enum).  Note that enums don't have types, so we use the 'any' type.
 *
 * @param myEnum the enum to search through.
 * @param enumValue the value to search for.
 *
 * @return the enum value matches or an empty array if no match was found.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getEnumsByEnumValue<E extends EnumType<E>>(myEnum: E, enumValue: EnumValue<E>): any[] {
  const enumMatches: EnumValue<E>[] = [];
  const enumKeys: EnumKey<E>[] = Object.keys(myEnum) as EnumKey<E>[];

  if (isNotEmptyArray(enumKeys)) {
    enumKeys.forEach((key: EnumKey<E>) => {
      if (myEnum[key] === enumValue) {
        enumMatches.push(myEnum[key]);
      }
    });
  }

  return enumMatches as E[];
}

/**
 * Reverse mapping function for string-based enums which returns the name for the first enum match for a given value
 * (values can match more than one instance of an enum).  Note that enums don't have types, so we use the 'any' type.
 *
 * @param myEnum the enum to search through.
 * @param enumValue the value to search for.
 *
 * @return the name of the enum value match or null if no matches were found.
 */
export function getEnumNameByEnumValue<E extends EnumType<E>>(myEnum: E, enumValue: EnumValue<E>): EnumKey<E> | null {
  const enumNames: EnumKey<E>[] = getEnumNamesByEnumValues(myEnum, [enumValue]);

  if (isNotEmptyArray(enumNames)) {
    return enumNames[0];
  }

  return null;
}

/**
 * Reverse mapping function for string-based enums which returns all names for enum matches for a given value
 * (values can match more than one instance of an enum).  Note that enums don't have types, so we use the 'any' type.
 *
 * @param myEnum the enum to search through.
 * @param enumValue the value to search for.
 *
 * @return the names of the enum value matches or an empty array if no matches were found.
 */
export function getEnumNamesByEnumValue<E extends EnumType<E>>(myEnum: E, enumValue: EnumValue<E>): EnumKey<E>[] {
  if (isValued(enumValue)) {
    return getEnumNamesByEnumValues<E>(myEnum, [enumValue]);
  }

  return [];
}

/**
 * Returns an enum as an {@link Array} of all the enums.
 *
 * @param myEnum the enum to iterate.
 *
 * @return an {@link Array} of all the enums.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getEnumAsArray<E extends EnumType<E>>(myEnum: E): any[] {
  return (Object.keys(myEnum) as EnumKey<E>[]).filter((key) => Number.isNaN(Number(key))).map((key) => myEnum[key]);
}

/**
 * Reverse mapping function for string-based enums which returns all names for enum matches for a list of given values
 * (values can match more than one instance of an enum).  Note that enums don't have types, so we use the 'any' type.
 *
 * @param myEnum the enum to search through.
 * @param enumValues the values to search for the keys to.
 *
 * @return the names of the enum value matches or an empty array if no matches were found.
 */
export function getEnumNamesByEnumValues<E extends EnumType<E>>(myEnum: E, enumValues: EnumValue<E>[]): EnumKey<E>[] {
  const enumNames: EnumKey<E>[] = [];
  const enumKeys: EnumKey<E>[] = Object.keys(myEnum) as EnumKey<E>[];

  if (isNotEmptyArray(enumKeys) && isNotEmptyArray(enumValues)) {
    for (let i = 0; i < enumValues.length; i++) {
      for (let j = 0; j < enumKeys.length; j++) {
        if (myEnum[enumKeys[j]] === enumValues[i]) {
          enumNames.push(enumKeys[j]);
          // Reminder we don't break here since a given value could match more than one enum.
        }
      }
    }
  }

  return enumNames;
}

/**
 * Simplistic algorithm to check whether a given enum is contained within a list of enums or not.
 *
 * @param enumList the list of enums to check.
 * @param toCheck the enum to check for.
 *
 * @return true if the given enum is found in the existing list.
 */
export function includes<E extends EnumType<E>>(enumList: EnumValue<E>[], toCheck: EnumValue<E>): boolean {
  if (isNotEmptyArray(enumList) && isValued(toCheck)) {
    for (let i = 0; i < enumList.length; i++) {
      if (enumList[i] === toCheck) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Allows for iteration of an enum while retaining the type of the enum.
 *
 * @param enumObjectType a reference to an enum.
 *
 * @return the key set of the enum, strongly typed.
 */
export function getEnumKeys<O extends object, K extends keyof O = keyof O>(enumObjectType: O): K[] {
  return Object.keys(enumObjectType).filter((k) => Number.isNaN(+k)) as K[];
}

// Example: export type NewType = EnumAsObject<typeof SomeEnum, SomeEnum>;
export type EnumAsObject<T, S extends string> = {
  [key in keyof T]: `${S}`;
};
